export type StageHead = 'Drawings' | 'PO' | 'Material' | 'Production';

export type DrawingsSubHead = 'Send' | 'Approved';
export type PoSubHead = 'Not Started' | 'Draft' | 'Placed';
export type MaterialSubHead = 'Not Recevied' | 'Partially Recevied' | 'Fully recevied';
export type ProductionSubHead = 
  | 'Cutter'
  | 'Laser'
  | 'welding'
  | 'Powder coating'
  | 'Assembly'
  | 'Busbar section'
  | 'Electrical'
  | 'Testing'
  | 'Ready';

export const STAGE_HEAD_SUBHEADS: Record<StageHead, string[]> = {
  'Drawings': ['Send', 'Approved'],
  'PO': ['Not Started', 'Draft', 'Placed'],
  'Material': ['Not Recevied', 'Partially Recevied', 'Fully recevied'],
  'Production': [
    'Cutter',
    'Laser',
    'welding',
    'Powder coating',
    'Assembly',
    'Busbar section',
    'Electrical',
    'Testing',
    'Ready'
  ]
};

export type ProjectStage = 
  | 'Drawing in Process'
  | 'Drawing Sent'
  | 'Drawing Approval Pending'
  | 'Drawing Approved'
  | 'Material Pending'
  | 'PO Placed'
  | 'Fabrication'
  | 'Powder Coating'
  | 'Electrical Wiring'
  | 'Testing'
  | 'Ready for Dispatch'
  | 'Dispatched'
  | 'Case on Hold'
  | 'Order Cancelled'
  // Head & SubHead stages
  | 'Drawings - Send'
  | 'Drawings - Approved'
  | 'PO - Not Started'
  | 'PO - Draft'
  | 'PO - Placed'
  | 'Material - Not Recevied'
  | 'Material - Partially Recevied'
  | 'Material - Fully recevied'
  | 'Production - Cutter'
  | 'Production - Laser'
  | 'Production - welding'
  | 'Production - Powder coating'
  | 'Production - Assembly'
  | 'Production - Busbar section'
  | 'Production - Electrical'
  | 'Production - Testing'
  | 'Production - Ready'
  | string;

export type PriorityLevel = 'Critical' | 'High' | 'Medium' | 'Low';

export interface MilestoneChecklist {
  id: string;
  title: string;
  completed: boolean;
  completedAt?: string;
  assignedTo?: string;
}

export interface FollowUpRecord {
  id: string;
  date: string;
  type: 'drawing_approval' | 'vendor_material' | 'payment' | 'internal_task' | 'dispatch';
  recipientEmail: string;
  recipientName: string;
  subject: string;
  body: string;
  status: 'sent' | 'scheduled' | 'draft' | 'failed';
  reminderNumber: number;
  deliveryMethod: 'smtp' | 'client_mailto' | 'system_log';
}

export interface ProjectLineItem {
  id: string;
  itemNumber?: string; // e.g. "1", "2", "SR-189"
  name: string; // e.g. "Main LT Panel (4000A ACB)" or "Spreader Terminal Kit 4P"
  qty: number; // e.g. 1, 30, 2
  unit: string; // e.g. "Nos", "Sets", "Pcs", "Mtrs"
  unitPrice: number; // Rate per unit in ₹
  totalPrice: number; // qty * unitPrice
  drawingStatus?: string; // e.g. "Approved", "In Process", "Pending"
  specifications?: string; // e.g. "4000A 4P ACB, 50kA, Form 4b"
}

export interface VendorPO {
  id: string;
  poNumber: string;               // e.g. "5464"
  poDate?: string;                // e.g. "2026-04-15"
  vendorName: string;             // e.g. "Havells", "Lauritz Knudsen (LK / L&T)"
  category?: string;              // e.g. "Switchgear", "Busbar / Copper", "Enclosure / Fabrication", "Meters & Relays", "Transformers", "Hardware", "General"
  concernedPerson?: string;       // Vendor contact person
  mobileNo?: string;              // Vendor phone
  emailId?: string;               // Vendor email
  amountWithoutGst?: number | null;
  gstRate?: number;               // e.g. 18 (%)
  gstAmount?: number | null;      // Calculated GST in ₹
  amountWithGst?: number | null;
  materialStatus?: string;        // e.g. "Received", "Pending 2 items", "Delivery on 15-09-26"
  status?: 'Pending' | 'Ready' | 'Placed' | 'Partially Received' | 'Fully Received' | string;
  expectedDate?: string;          // Delivery commitment
  remarks?: string;
  // Excel File Upload & Line-Item Tracking
  uploadedFileName?: string;      // e.g. "Havells_PO_5464.xlsx"
  uploadedAt?: string;            // ISO timestamp
  items?: POLineItem[];           // Items imported from Excel or entered
  followUpLogs?: POFollowUpLog[]; // Log of interactions by Follow-ups team
  pdfSavedToFolder?: boolean;
  pdfFileName?: string;
}

export interface VendorProductItem {
  id: string;
  name: string;
  code?: string;
  category?: string;
  unitRate?: number;
  unit?: string;
  hsnCode?: string;
  leadTime?: string;
  description?: string;
}

export interface VendorMasterItem {
  id: string;
  vendorCode: string;             // e.g. "VND-001"
  name: string;                   // Company Name e.g. "Havells India Ltd."
  category: string;               // Switchgear, Busbar, Enclosure, Meters, Transformers, Cables, Hardware, etc.
  contactPerson: string;          // Primary contact person
  designation?: string;           // Sales Manager, Director, etc.
  phone: string;                  // Primary phone / mobile
  phone2?: string;                // Secondary phone
  altPhone?: string;              // Landline or alternate mobile
  otherPhone?: string;            // Other phone / emergency
  email: string;                  // Official email for PO dispatch
  ccEmail?: string;               // Secondary email
  address: string;                // Factory or Office address
  city: string;                   // City (e.g. Mumbai, Delhi, Thane)
  state: string;                  // State (e.g. Maharashtra, Delhi, Gujarat)
  gstin: string;                  // GSTIN No.
  pan?: string;                   // PAN No.
  paymentTerms: string;           // Agreed payment terms (e.g. 30 Days Credit, Against PI)
  leadTime: string;               // Standard delivery lead time (e.g. 7-10 Days, Ex-Stock)
  bankDetails?: {
    bankName: string;
    accountNo: string;
    ifscCode: string;
    branch: string;
  };
  status: 'Approved' | 'Preferred' | 'Regular' | 'On Hold';
  rating?: number;                // 1 to 5
  notes?: string;                 // Technical scope & notes
  createdDate?: string;
  companiesDealtIn?: string[];    // Brands / Companies the vendor deals in e.g. ["LK / L&T", "Schneider", "Havells"]
  productsDealtIn?: string[];     // Products / items the vendor deals in e.g. ["ACB", "MCCB", "Power Contactor", "Busbar"]
  productsSupplied?: VendorProductItem[]; // List of catalog items / materials supplied
}

export interface POLineItem {
  id: string;
  srNo?: number | string;         // Serial number (e.g. 1, 2, ...)
  groupName?: string;             // Group / Category (e.g. ACB, MCB, CAPACITOR, POWER CONTACTOR)
  make?: string;                  // Brand / Manufacturer (e.g. LEGRAND, SCHNEIDER, L&T)
  itemCode?: string;              // Cat No / Part Number / Catalog Number
  description: string;            // Item specification / description (e.g. DESCRIPTION-2)
  orderedQty: number;             // Total quantity ordered
  unit: string;                   // Nos, Sets, Mtr, Pcs, etc.
  listPrice?: number;             // List price (PRICE before discount) in ₹
  discountPercent?: number;       // Discount % (e.g. 68)
  discountAmount?: number;        // Discount amount in ₹
  landedRate?: number;            // Landed rate (net buying price per unit) in ₹
  landedCost?: number;            // Landed cost (net total item cost) in ₹
  unitPrice?: number;             // Effective unit rate (landed rate) in ₹
  totalPrice?: number;            // Effective total basic amount (landed cost) in ₹
  expectedDate?: string;          // Vendor delivery commitment date
  // Store Team Tracking Fields
  receivedQty: number;            // Quantity physically received in store
  pendingQty: number;             // Balance quantity pending from vendor
  status: 'Pending' | 'Partially Received' | 'Received' | 'Delayed';
  storeInwardNumber?: string;     // MRN / Gate Pass / Challan Number
  storeInwardDate?: string;       // Date received at plant
  storeRemarks?: string;          // Store keeper inspection remark
  // Follow-ups Team Tracking Fields
  nextFollowUpDate?: string;      // Scheduled next follow-up date
  followUpNotes?: string;         // Last follow-up note / dispatch commitment
  followedUpBy?: string;          // Name of person who followed up
}

export interface POFollowUpLog {
  id: string;
  date: string;                   // Date & time of follow-up (YYYY-MM-DD or readable)
  loggedBy: string;               // Follow-up team member name
  contactPerson?: string;         // Vendor representative spoken to
  method: 'Phone Call' | 'WhatsApp' | 'Email' | 'Plant Visit' | 'Other';
  summary: string;                // Discussion details / commitment given
  nextCommitmentDate?: string;    // Vendor promised dispatch/delivery date
}

// Helpers for PO Line Items and Delivery tracking
export function calculatePOItemStats(items?: POLineItem[]) {
  if (!items || items.length === 0) {
    return {
      totalItems: 0,
      totalOrderedQty: 0,
      totalReceivedQty: 0,
      totalPendingQty: 0,
      fullyReceivedCount: 0,
      partiallyReceivedCount: 0,
      pendingCount: 0,
      percentDelivered: 0
    };
  }

  let totalOrderedQty = 0;
  let totalReceivedQty = 0;
  let fullyReceivedCount = 0;
  let partiallyReceivedCount = 0;
  let pendingCount = 0;

  items.forEach(it => {
    const ord = Number(it.orderedQty) || 0;
    const rec = Number(it.receivedQty) || 0;
    totalOrderedQty += ord;
    totalReceivedQty += Math.min(rec, ord);

    if (rec >= ord && ord > 0) {
      fullyReceivedCount++;
    } else if (rec > 0) {
      partiallyReceivedCount++;
    } else {
      pendingCount++;
    }
  });

  const totalPendingQty = Math.max(0, totalOrderedQty - totalReceivedQty);
  const percentDelivered = totalOrderedQty > 0 ? Math.round((totalReceivedQty / totalOrderedQty) * 100) : 0;

  return {
    totalItems: items.length,
    totalOrderedQty,
    totalReceivedQty,
    totalPendingQty,
    fullyReceivedCount,
    partiallyReceivedCount,
    pendingCount,
    percentDelivered
  };
}

export function formatPOItemSummary(items?: POLineItem[]): string {
  const stats = calculatePOItemStats(items);
  if (stats.totalItems === 0) return '';
  if (stats.percentDelivered === 100) {
    return `All ${stats.totalItems} items received (100%)`;
  }
  if (stats.totalReceivedQty === 0) {
    return `${stats.totalItems} items pending (${stats.totalOrderedQty} Qty)`;
  }
  return `${stats.totalReceivedQty}/${stats.totalOrderedQty} Qty received (${stats.percentDelivered}%) • ${stats.pendingCount} pending`;
}

export interface TeamMemberProfile {
  id: string;
  name: string;
  role: string;
  email: string;
  phone: string;
  department?: string;
  specialization?: 'PO Incharge' | 'Drawings Incharge' | 'Design Incharge' | 'Production Incharge' | 'Commercial & Management' | 'Testing & Quality' | 'General';
  avatarColor?: string;
  active?: boolean;
}

export interface ProjectConcernedRoles {
  poIncharge?: string;
  drawingsIncharge?: string;
  designIncharge?: string;
  productionIncharge?: string;
}

export interface ProjectRoleAssignment {
  id?: string;
  role: string; // e.g. 'PO Incharge', 'Drawings Incharge', 'Design Incharge', 'Production Incharge', 'Testing Incharge', etc.
  person: string; // Team member name (e.g. 'Akshay', 'Vishal', etc.)
  department?: string; // e.g. 'Procurement', 'CAD & GA', 'Engineering', 'Fabrication', 'Quality'
  badgeColor?: string; // e.g. 'purple', 'blue', 'indigo', 'amber', 'emerald', 'cyan', 'rose'
}

export interface RolePreset {
  role: string;
  department: string;
  badgeColor: string;
}

export const CORE_ROLE_PRESETS: RolePreset[] = [
  { role: 'PO Incharge', department: 'Procurement', badgeColor: 'purple' },
  { role: 'Drawings Incharge', department: 'CAD & GA', badgeColor: 'blue' },
  { role: 'Design Incharge', department: 'Engineering', badgeColor: 'indigo' },
  { role: 'Production Incharge', department: 'Fabrication', badgeColor: 'amber' },
  { role: 'Testing & Quality Incharge', department: 'Quality', badgeColor: 'emerald' },
  { role: 'Commercial & Billing Incharge', department: 'Accounts', badgeColor: 'cyan' },
  { role: 'Site & Commissioning Incharge', department: 'Site Works', badgeColor: 'rose' },
  { role: 'Client Coordination Incharge', department: 'Coordination', badgeColor: 'teal' }
];

export const STANDARD_TASK_ROLES = [
  'PO Incharge',
  'Drawings Incharge',
  'Design Incharge',
  'Production Incharge'
] as const;

export type StandardTaskRole = typeof STANDARD_TASK_ROLES[number];

export type DocumentCategory =
  | 'Work Order / PO'
  | 'Approved Drawing / GA'
  | 'SLD / Technical Spec'
  | 'Quotation / Commercial'
  | 'Inspection / TC'
  | 'Other Document';

export const DEFAULT_DOCUMENT_FOLDERS = [
  'Client PO & Work Orders',
  'Approved GA Drawings',
  'Electrical SLD & Schematics',
  'BOM & Cutting Lists',
  'Vendor Quotations',
  'Testing & Inspection (TC)',
  'Dispatch & Invoices',
  'General / Site Updates'
] as const;

export type DefaultDocumentFolder = typeof DEFAULT_DOCUMENT_FOLDERS[number];

export interface ProjectDocument {
  id: string;
  name: string;
  size: number;
  type: string;
  category: DocumentCategory;
  folder?: string;
  uploadedAt: string;
  uploadedBy?: string;
  dataUrl?: string;
  notes?: string;
  status?: "Approved" | "Rejected" | "Pending review";
}

export interface WizardFieldCompulsoryConfig {
  // Step 1: Customer Details
  partyName: boolean;       // Customer / Company Name
  contactPerson: boolean;   // Contact Person
  contactPhone: boolean;    // Mobile / Phone No.
  partyEmail: boolean;      // Email Address
  caNumber: boolean;        // CA No. / Work Order Ref

  // Step 2: Project & Commercial Details
  dveplCode: boolean;       // DVEPL Job Code
  orderConfirmDate: boolean;// Order Confirm Date
  deliveryTarget: boolean;  // Delivery Target
  orderTakenBy: boolean;    // Order Taken By
  amount: boolean;          // Order Value / Amount
  remark: boolean;          // Remarks

  // Step 3: Schedule of Items & Rates
  items: boolean;           // Line Items & Rates
  itemDescription: boolean; // Item Description

  // Step 4: Core Roles & Drawings
  drawingTargetDate: boolean;  // Target Date to Send Drawings
  drawingsIncharge: boolean;   // Drawings Incharge
  poIncharge: boolean;         // PO Incharge
  designIncharge: boolean;     // Design Incharge
  productionIncharge: boolean; // Production Incharge
  concernedPersons?: boolean;  // Additional Concerned Persons

  // Step 5: Upload Documents
  documents: boolean;          // Upload at least 1 document
}

export const DEFAULT_WIZARD_FIELD_COMPULSORY_CONFIG: WizardFieldCompulsoryConfig = {
  // Step 1
  partyName: true,
  contactPerson: false,
  contactPhone: true,
  partyEmail: true,
  caNumber: false,
  // Step 2
  dveplCode: true,
  orderConfirmDate: true,
  deliveryTarget: true,
  orderTakenBy: false,
  amount: false,
  remark: false,
  // Step 3
  items: false,
  itemDescription: false,
  // Step 4
  drawingTargetDate: true,
  drawingsIncharge: true,
  poIncharge: false,
  designIncharge: false,
  productionIncharge: false,
  concernedPersons: false,
  // Step 5
  documents: false
};

export interface WizardCompulsoryConfig {
  customer: boolean;     // Step 1: Customer Details (Party name, phone/email)
  project: boolean;      // Step 2: Project & Commercial Details (Job code, dates, value)
  schedule: boolean;     // Step 3: Schedule of Items & Rates (at least 1 line item)
  roles: boolean;        // Step 4: Core Roles & Drawing Target Date (target date & incharges)
  documents: boolean;    // Step 5: Upload Documents (at least 1 document uploaded in folders)
  fields?: WizardFieldCompulsoryConfig; // Granular per-field compulsory configuration
}

export const DEFAULT_WIZARD_COMPULSORY_CONFIG: WizardCompulsoryConfig = {
  customer: true,
  project: true,
  schedule: false,
  roles: true,
  documents: false,
  fields: DEFAULT_WIZARD_FIELD_COMPULSORY_CONFIG
};

export const STORAGE_KEY_WIZARD_COMPULSORY = 'dvepl_wizard_compulsory_tabs';
export const STORAGE_KEY_WIZARD_DRAFT = 'dvepl_job_wizard_draft';

export interface ProjectTask {
  id: string;
  sNo: string;
  dveplCode: string;
  orderConfirmDate: string;
  orderTakenBy: string;
  deliveryTarget: string;
  concernedPerson: string; // Assigned engineer (e.g. "Vishal", or "Vishal, Akshay")
  concernedPersons?: string[]; // Multiple assigned engineers list
  concernedRoles?: ProjectConcernedRoles; // 1) PO Incharge, 2) Drawings Incharge, 3) Design Incharge, 4) Production Incharge
  projectRoleAssignments?: ProjectRoleAssignment[]; // Assigned roles
  concernedEmail?: string;
  concernedEmails?: string[];
  partyName: string; // Client / Company name
  companyName?: string; // Explicit company name
  contactPerson?: string; // Client Contact Person (e.g. "Mr. Onkar Nath Bhalla")
  contactPhone?: string; // Mobile / Phone
  mobileNo?: string; // Explicit mobile no alias
  partyEmail?: string; // Client email
  emailId?: string; // Explicit email ID alias
  caNumber: string; // Contract / Work Order No.
  itemDescription: string; // Panels summary list
  items?: ProjectLineItem[]; // Detailed line items with distinct names, qty, unit prices, and totals
  amount: number;
  gst: number;
  totalAmount: number;
  advance: number;
  balance: number;
  stage: ProjectStage;
  stageHead?: StageHead;
  stageSubHead?: string;
  stageDrawings?: DrawingsSubHead | string;
  stagePo?: PoSubHead | string;
  stageMaterial?: MaterialSubHead | string;
  stageProduction?: ProductionSubHead | string;
  priority: PriorityLevel;
  materialStatus: string; // e.g. "MCCB received", "In fabrication"
  vendorName?: string; // Havells, LK, HPL, Indoasian, etc.
  vendorContactPerson?: string; // Vendor Concerned Person
  vendorMobileNo?: string; // Vendor Mobile No
  vendorPhone?: string; // Vendor Phone alias
  vendorEmail?: string;
  poNumber?: string;
  poDate?: string;
  poAmount?: number;
  poAmountWithGst?: number;
  vendorPOs?: VendorPO[]; // Multiple POs support: one order can place multiple POs to different vendors
  hasCreatedPO?: boolean;
  drawingTargetDate?: string; // Target date to send drawings (task deadline for drawing engineer)
  drawingApprovedDate?: string;
  drawingSendDate?: string;
  billNumber?: string;
  billDate?: string;
  dispatchDate?: string;
  transporterName?: string;
  lrNumber?: string;
  remark: string;
  milestones: MilestoneChecklist[];
  followUpCount: number;
  // Time-based Job Monitoring & Efficiency Tracking
  allottedHours?: number; // Total hours allotted to complete this job
  actualHours?: number; // Total hours actually taken to complete or elapsed
  efficiency?: number; // Calculated efficiency percentage
  timeLogs?: JobTimeLog[]; // Subtask level time breakdown
  lastFollowUpDate?: string;
  nextFollowUpDate?: string;
  autoFollowUpEnabled: boolean;
  followUpCadenceDays: number; // e.g. every 3 days
  followUpHistory: FollowUpRecord[];
  documents?: ProjectDocument[];
  customFolders?: string[];
  isDraft?: boolean;
  savedStep?: number;
  draftSavedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface JobTimeLog {
  id: string;
  taskTitle: string; // e.g. "Drawing Preparation", "BOM & PO Placement", "Fabrication & Assembly", "Testing & QC"
  assignedMember: string; // e.g. "Vishal", "Akshay", "Arun", "Lakhwinder"
  allottedHours: number;
  actualHours: number;
  status: 'Completed' | 'In Progress' | 'Pending';
  dateCompleted?: string;
  notes?: string;
}

export function calculateJobEfficiency(allotted?: number, actual?: number): {
  efficiencyPercent: number;
  label: string;
  color: string;
  badgeBg: string;
  status: 'ahead' | 'on-track' | 'lagging' | 'overdue';
} {
  const allott = Number(allotted) || 0;
  const act = Number(actual) || 0;
  if (allott === 0 || act === 0) {
    return {
      efficiencyPercent: 100,
      label: 'Standard',
      color: 'text-slate-600',
      badgeBg: 'bg-slate-100 text-slate-700 border-slate-200',
      status: 'on-track'
    };
  }
  const pct = Math.round((allott / act) * 100);
  if (pct >= 110) {
    return {
      efficiencyPercent: pct,
      label: `${pct}% (Fast / Ahead)`,
      color: 'text-emerald-700',
      badgeBg: 'bg-emerald-50 text-emerald-800 border-emerald-300',
      status: 'ahead'
    };
  } else if (pct >= 95) {
    return {
      efficiencyPercent: pct,
      label: `${pct}% (On Target)`,
      color: 'text-teal-700',
      badgeBg: 'bg-teal-50 text-teal-800 border-teal-300',
      status: 'on-track'
    };
  } else if (pct >= 80) {
    return {
      efficiencyPercent: pct,
      label: `${pct}% (Moderate Delay)`,
      color: 'text-amber-700',
      badgeBg: 'bg-amber-50 text-amber-800 border-amber-300',
      status: 'lagging'
    };
  } else {
    return {
      efficiencyPercent: pct,
      label: `${pct}% (Critical Delay)`,
      color: 'text-rose-700',
      badgeBg: 'bg-rose-50 text-rose-800 border-rose-300',
      status: 'overdue'
    };
  }
}

export type Project = ProjectTask;

export interface EmailDraftRequest {
  projectId: string;
  type: 'drawing_approval' | 'vendor_material' | 'payment' | 'internal_task' | 'dispatch';
  tone: 'polite' | 'firm' | 'urgent';
  customNotes?: string;
}

export interface SendEmailPayload {
  to: string;
  cc?: string;
  subject: string;
  htmlBody: string;
  textBody?: string;
  projectId?: string;
  followUpType?: string;
  reminderNumber?: number;
  senderName?: string;
}

export interface SmtpConfig {
  host: string;
  port: number;
  secure: boolean;
  user: string;
  pass: string;
  fromName: string;
  fromEmail: string;
  isConfigured: boolean;
}

export function getProjectConcernedRoles(project: ProjectTask | null | undefined): {
  poIncharge: string;
  drawingsIncharge: string;
  designIncharge: string;
  productionIncharge: string;
} {
  if (!project) {
    return { poIncharge: '-', drawingsIncharge: '-', designIncharge: '-', productionIncharge: '-' };
  }
  if (project.concernedRoles) {
    return {
      poIncharge: project.concernedRoles.poIncharge || '-',
      drawingsIncharge: project.concernedRoles.drawingsIncharge || '-',
      designIncharge: project.concernedRoles.designIncharge || '-',
      productionIncharge: project.concernedRoles.productionIncharge || '-'
    };
  }
  if (Array.isArray(project.projectRoleAssignments) && project.projectRoleAssignments.length > 0) {
    const po = project.projectRoleAssignments.find(r => r.role.toLowerCase().includes('po'))?.person;
    const drg = project.projectRoleAssignments.find(r => r.role.toLowerCase().includes('draw'))?.person;
    const des = project.projectRoleAssignments.find(r => r.role.toLowerCase().includes('design'))?.person;
    const prod = project.projectRoleAssignments.find(r => r.role.toLowerCase().includes('prod') || r.role.toLowerCase().includes('cut'))?.person;
    return {
      poIncharge: po || 'Deepanshu',
      drawingsIncharge: drg || 'Akshay',
      designIncharge: des || 'Vishal',
      productionIncharge: prod || 'Arun'
    };
  }
  const persons = getConcernedPersons(project);
  return {
    poIncharge: 'Deepanshu',
    drawingsIncharge: persons[1] || 'Akshay',
    designIncharge: persons[0] || 'Vishal',
    productionIncharge: 'Arun'
  };
}

export function getConcernedPersons(project: { concernedPerson?: string; concernedPersons?: string[]; concernedRoles?: ProjectConcernedRoles; projectRoleAssignments?: ProjectRoleAssignment[] } | null | undefined): string[] {
  if (!project) return [];
  if (project.concernedRoles) {
    const fromRoles = [
      project.concernedRoles.poIncharge,
      project.concernedRoles.drawingsIncharge,
      project.concernedRoles.designIncharge,
      project.concernedRoles.productionIncharge
    ].filter((p): p is string => Boolean(p && p !== '-'));
    if (fromRoles.length > 0) return Array.from(new Set(fromRoles));
  }
  if (Array.isArray(project.projectRoleAssignments) && project.projectRoleAssignments.length > 0) {
    const list = project.projectRoleAssignments.map(ra => ra.person).filter(Boolean);
    if (list.length > 0) {
      return Array.from(new Set(list));
    }
  }
  if (Array.isArray(project.concernedPersons) && project.concernedPersons.length > 0) {
    return project.concernedPersons.filter(Boolean);
  }
  if (project.concernedPerson && typeof project.concernedPerson === 'string') {
    const list = project.concernedPerson.split(',').map(s => s.trim()).filter(Boolean);
    if (list.length > 0) return list;
  }
  return ['Unassigned'];
}

export function getProjectRoleAssignments(project: ProjectTask | null | undefined): ProjectRoleAssignment[] {
  if (!project) return [];
  if (Array.isArray(project.projectRoleAssignments) && project.projectRoleAssignments.length > 0) {
    return project.projectRoleAssignments.filter(ra => ra.person && ra.person.trim() && ra.person !== '-' && ra.person !== 'Unassigned');
  }
  const roles = getProjectConcernedRoles(project);
  const list: ProjectRoleAssignment[] = [
    { role: 'PO Incharge', person: roles.poIncharge, department: 'Procurement', badgeColor: 'purple' },
    { role: 'Drawings Incharge', person: roles.drawingsIncharge, department: 'CAD & GA', badgeColor: 'blue' },
    { role: 'Design Incharge', person: roles.designIncharge, department: 'Engineering', badgeColor: 'indigo' },
    { role: 'Production Incharge', person: roles.productionIncharge, department: 'Fabrication', badgeColor: 'amber' }
  ];
  return list.filter(r => r.person && r.person !== '-' && r.person !== 'Unassigned');
}

export type DrawingTaskStatus = 'completed' | 'on_time' | 'due_today' | 'overdue' | 'not_set';

export interface DrawingTaskInfo {
  status: DrawingTaskStatus;
  label: string;
  targetDateStr: string;
  rawTargetDate: string;
  assignedTo: string;
  isCompleted: boolean;
  daysRemainingOrOverdue: number;
  color: 'emerald' | 'amber' | 'rose' | 'slate' | 'blue';
}

export function getDrawingTaskInfo(project: ProjectTask | null | undefined): DrawingTaskInfo {
  if (!project) {
    return {
      status: 'not_set',
      label: 'No Target Set',
      targetDateStr: '-',
      rawTargetDate: '',
      assignedTo: 'Unassigned',
      isCompleted: false,
      daysRemainingOrOverdue: 0,
      color: 'slate'
    };
  }

  const assignedTo = project.concernedRoles?.drawingsIncharge ||
    project.projectRoleAssignments?.find(r => r.role.toLowerCase().includes('draw'))?.person ||
    'Akshay';

  const isCompleted = !!(
    project.drawingApprovedDate ||
    (project.drawingSendDate && project.drawingSendDate !== '-' && project.stage !== 'Drawing in Process') ||
    project.stage === 'Dispatched'
  );

  const rawTargetDate = project.drawingTargetDate || project.drawingSendDate || '';
  if (!rawTargetDate || rawTargetDate === '-') {
    return {
      status: isCompleted ? 'completed' : 'not_set',
      label: isCompleted ? 'Drawings Done' : 'No Target Set',
      targetDateStr: isCompleted ? (formatToDDMMYY(project.drawingSendDate) || 'Completed') : '-',
      rawTargetDate: '',
      assignedTo,
      isCompleted,
      daysRemainingOrOverdue: 0,
      color: isCompleted ? 'emerald' : 'slate'
    };
  }

  if (isCompleted) {
    return {
      status: 'completed',
      label: 'Completed',
      targetDateStr: formatToDDMMYY(rawTargetDate),
      rawTargetDate,
      assignedTo,
      isCompleted: true,
      daysRemainingOrOverdue: 0,
      color: 'emerald'
    };
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  let target: Date | null = null;
  if (rawTargetDate.includes('-') && rawTargetDate.split('-')[0].length === 4) {
    target = new Date(rawTargetDate);
  } else {
    const parts = rawTargetDate.split(/[-/]/);
    if (parts.length === 3) {
      const d = parseInt(parts[0], 10);
      const m = parseInt(parts[1], 10) - 1;
      let y = parseInt(parts[2], 10);
      if (y < 100) y += 2000;
      target = new Date(y, m, d);
    }
  }

  if (!target || isNaN(target.getTime())) {
    return {
      status: 'not_set',
      label: rawTargetDate,
      targetDateStr: rawTargetDate,
      rawTargetDate,
      assignedTo,
      isCompleted: false,
      daysRemainingOrOverdue: 0,
      color: 'slate'
    };
  }

  target.setHours(0, 0, 0, 0);
  const diffTime = target.getTime() - today.getTime();
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    return {
      status: 'overdue',
      label: `Overdue by ${Math.abs(diffDays)}d`,
      targetDateStr: formatToDDMMYY(rawTargetDate),
      rawTargetDate,
      assignedTo,
      isCompleted: false,
      daysRemainingOrOverdue: diffDays,
      color: 'rose'
    };
  } else if (diffDays === 0) {
    return {
      status: 'due_today',
      label: 'Due Today',
      targetDateStr: formatToDDMMYY(rawTargetDate),
      rawTargetDate,
      assignedTo,
      isCompleted: false,
      daysRemainingOrOverdue: 0,
      color: 'amber'
    };
  } else if (diffDays <= 2) {
    return {
      status: 'on_time',
      label: `Due in ${diffDays}d`,
      targetDateStr: formatToDDMMYY(rawTargetDate),
      rawTargetDate,
      assignedTo,
      isCompleted: false,
      daysRemainingOrOverdue: diffDays,
      color: 'amber'
    };
  } else {
    return {
      status: 'on_time',
      label: `On Time (${diffDays}d left)`,
      targetDateStr: formatToDDMMYY(rawTargetDate),
      rawTargetDate,
      assignedTo,
      isCompleted: false,
      daysRemainingOrOverdue: diffDays,
      color: 'emerald'
    };
  }
}

export function getProjectItems(project: ProjectTask | null | undefined): ProjectLineItem[] {
  if (!project) return [];
  if (Array.isArray(project.items) && project.items.length > 0) {
    return project.items;
  }

  // Fallback: automatically parse from itemDescription if available
  if (project.itemDescription) {
    // E.g. "SR-NO 189 (1 Nos), SRNO-177 (30 Nos), SRNO-190 (2 Nos)"
    const rawParts = project.itemDescription.split(',').map(s => s.trim()).filter(Boolean);
    if (rawParts.length > 0) {
      const totalAmount = project.amount || 0;
      return rawParts.map((part, idx) => {
        const qtyMatch = part.match(/\((\d+)\s*([a-zA-Z]+)?\)/);
        const qty = qtyMatch ? parseInt(qtyMatch[1], 10) : 1;
        const unit = (qtyMatch && qtyMatch[2]) ? qtyMatch[2] : 'Nos';
        const cleanedName = part.replace(/\(\d+\s*[a-zA-Z]*\)/, '').trim();
        const itemTotal = rawParts.length === 1 ? totalAmount : Math.round(totalAmount / rawParts.length);
        const unitPrice = qty > 0 ? Math.round(itemTotal / qty) : itemTotal;

        return {
          id: `item-${project.id}-${idx + 1}`,
          itemNumber: `${idx + 1}`,
          name: cleanedName || part,
          qty: qty || 1,
          unit: unit || 'Nos',
          unitPrice: unitPrice,
          totalPrice: itemTotal
        };
      });
    }
  }

  return [
    {
      id: `item-${project.id}-1`,
      itemNumber: '1',
      name: project.itemDescription || 'Standard Switchgear Panel',
      qty: 1,
      unit: 'Nos',
      unitPrice: project.amount || 0,
      totalPrice: project.amount || 0
    }
  ];
}

export function calculateItemsTotal(items: ProjectLineItem[]): number {
  return items.reduce((sum, item) => sum + (Number(item.totalPrice) || (Number(item.qty || 1) * Number(item.unitPrice || 0))), 0);
}

export function formatItemsSummary(items: ProjectLineItem[]): string {
  if (!items || items.length === 0) return '';
  return items
    .map(i => `${i.name} (${i.qty} ${i.unit || 'Nos'} @ ₹${(i.unitPrice || 0).toLocaleString('en-IN')})`)
    .join(', ');
}

export interface ProjectDetailsSummary {
  totalItems: number;
  totalQty: number;
  totalAmountWithGst: number;
  totalAmountWithoutGst: number;
  advance: number;
  balance: number;
}

export function getProjectDetailsSummary(project: ProjectTask | null | undefined): ProjectDetailsSummary {
  if (!project) {
    return {
      totalItems: 0,
      totalQty: 0,
      totalAmountWithGst: 0,
      totalAmountWithoutGst: 0,
      advance: 0,
      balance: 0
    };
  }

  const items = getProjectItems(project);
  const totalItems = items.length > 0 ? items.length : 1;
  const totalQty = items.reduce((sum, it) => sum + (Number(it.qty) || 0), 0) || 1;
  const totalAmountWithoutGst = Number(project.amount) || calculateItemsTotal(items);
  const gstAmount = project.gst !== undefined && project.gst !== null
    ? Number(project.gst)
    : Math.round(totalAmountWithoutGst * 0.18);
  const totalAmountWithGst = Number(project.totalAmount) || (totalAmountWithoutGst + gstAmount);
  const advance = Number(project.advance) || 0;
  const balance = project.balance !== undefined && project.balance !== null
    ? Number(project.balance)
    : Math.max(0, totalAmountWithGst - advance);

  return {
    totalItems,
    totalQty,
    totalAmountWithGst,
    totalAmountWithoutGst,
    advance,
    balance
  };
}

export interface PartyDetails {
  companyName: string;
  contactPerson: string;
  mobileNo: string;
  emailId: string;
}

export function getPartyDetails(project: ProjectTask | null | undefined): PartyDetails {
  if (!project) {
    return {
      companyName: '',
      contactPerson: '',
      mobileNo: '',
      emailId: ''
    };
  }

  const rawParty = (project.companyName || project.partyName || '').trim();
  const companyName = rawParty;

  // 1. Contact Person: Leave blank if not available
  const contactPerson = (project.contactPerson || '').trim();

  // 2. Mobile no: Leave blank if not available
  let mobileNo = (project.mobileNo || project.contactPhone || '').trim();
  if (mobileNo === '-') mobileNo = '';

  // 3. Email ID: Leave blank if not available
  let emailId = (project.emailId || project.partyEmail || '').trim();
  if (emailId === '-') emailId = '';

  return {
    companyName,
    contactPerson,
    mobileNo,
    emailId
  };
}

export interface VendorDetailsInfo {
  vendorName: string;
  concernedPerson: string;
  mobileNo: string;
  emailId: string;
  poNumberAndDate: string;
  poNumber: string;
  poDate: string;
  amountWithoutGst: number | null;
  amountWithGst: number | null;
}

export const KNOWN_VENDOR_DIRECTORY: Record<string, { contact: string; phone: string; email: string; category?: string }> = {
  'havells': { contact: 'Sumeet Dubey', phone: '+91 98100 23456', email: 'switchgear.orders@havells.com', category: 'Switchgear' },
  'lauritz knudsen (lk / l&t)': { contact: 'Sumit Chinouria / Dushant', phone: '+91 98180 54321', email: 'orders@lauritzknudsen.com', category: 'Switchgear' },
  'lauritz knudsen': { contact: 'Sumit Chinouria', phone: '+91 98180 54321', email: 'orders@lauritzknudsen.com', category: 'Switchgear' },
  'lk': { contact: 'Sumit Chinouria', phone: '+91 98180 54321', email: 'orders@lauritzknudsen.com', category: 'Switchgear' },
  'l&t': { contact: 'Sumit Chinouria', phone: '+91 98180 54321', email: 'orders@lauritzknudsen.com', category: 'Switchgear' },
  'hpl electric': { contact: 'Navjot Singh', phone: '+91 98111 67890', email: 'delhi.sales@hplindia.com', category: 'Switchgear / Meters' },
  'hpl': { contact: 'Navjot Singh', phone: '+91 98111 67890', email: 'delhi.sales@hplindia.com', category: 'Switchgear / Meters' },
  'indoasian': { contact: 'Vishal Sandhu', phone: '+91 98720 11223', email: 'support@indoasian.com', category: 'Switchgear' },
  'benlo': { contact: 'Sandeep Mehta', phone: '+91 98105 44332', email: 'orders@benloelectric.com', category: 'Switchgear' },
  'schneider electric': { contact: 'Mohd Yameen', phone: '+91 99100 88776', email: 'orders.india@se.com', category: 'Switchgear' },
  'schneider': { contact: 'Mohd Yameen', phone: '+91 99100 88776', email: 'orders.india@se.com', category: 'Switchgear' },
  'unikon transformers': { contact: 'Ujjwal', phone: '+91 98110 33221', email: 'sales@unikon.in', category: 'Transformers & VCB' },
  'unikon': { contact: 'Ujjwal', phone: '+91 98110 33221', email: 'sales@unikon.in', category: 'Transformers & VCB' },
  'bs electrical delhi': { contact: 'B.S. Rep', phone: '+91 98101 99887', email: 'bselectrical.delhi@gmail.com', category: 'Components' },
  'abb india': { contact: 'Ritik Sir', phone: '+91 98112 55443', email: 'contact.abb@in.abb.com', category: 'Switchgear' },
  'jindal steel': { contact: 'Rakesh Sharma', phone: '+91 98102 33445', email: 'enclosures@jindal.com', category: 'Enclosure / Fabrication' },
  'hindalco copper': { contact: 'Manoj Verma', phone: '+91 98103 44556', email: 'busbar.sales@hindalco.com', category: 'Copper Busbar' },
  'rishabh instruments': { contact: 'Ketan Patel', phone: '+91 98104 55667', email: 'meters@rishabh.co.in', category: 'Meters & Relays' },
  'polycab': { contact: 'Ajay Gupta', phone: '+91 98105 66778', email: 'cables@polycab.com', category: 'Control Cables' }
};

export function lookupVendorContact(vendorName: string) {
  if (!vendorName) return null;
  const vKey = vendorName.toLowerCase().trim();
  return KNOWN_VENDOR_DIRECTORY[vKey] || Object.entries(KNOWN_VENDOR_DIRECTORY).find(([k]) => vKey.includes(k))?.[1] || null;
}

export function getProjectPOs(project: ProjectTask | null | undefined): VendorPO[] {
  if (!project) return [];
  if (project.hasCreatedPO === false) return [];
  if (Array.isArray(project.vendorPOs) && project.vendorPOs.length > 0) {
    return project.vendorPOs;
  }
  
  // Backward compatibility: synthesize PO(s) from legacy vendor fields
  const vendorName = (project.vendorName || '').trim();
  const poNumber = (project.poNumber || '').trim();
  
  if (!vendorName && !poNumber) return [];

  // Check if poNumber has multiple PO numbers separated by '/' or '&' e.g. "5395 / 5532"
  if (poNumber.includes('/') || poNumber.includes('&')) {
    const rawParts = poNumber.split(/[/&]+/).map(s => s.trim()).filter(Boolean);
    if (rawParts.length > 1) {
      const vDetails = getVendorDetails(project);
      return rawParts.map((poNum, idx) => {
        return {
          id: `po-${project.id}-${idx + 1}`,
          poNumber: poNum,
          poDate: project.poDate || '',
          vendorName: idx === 0 ? (project.vendorName || 'Vendor') : (project.vendorName || 'Vendor 2'),
          category: 'Switchgear',
          concernedPerson: vDetails.concernedPerson || '',
          mobileNo: vDetails.mobileNo || '',
          emailId: vDetails.emailId || '',
          amountWithoutGst: idx === 0 ? project.poAmount : null,
          amountWithGst: idx === 0 ? project.poAmountWithGst : null,
          materialStatus: project.materialStatus || ''
        };
      });
    }
  }

  const vDetails = getVendorDetails(project);
  return [{
    id: `po-${project.id}-1`,
    poNumber: project.poNumber || '-',
    poDate: project.poDate || '',
    vendorName: project.vendorName || 'Vendor',
    category: 'Switchgear',
    concernedPerson: vDetails.concernedPerson || '',
    mobileNo: vDetails.mobileNo || '',
    emailId: vDetails.emailId || '',
    amountWithoutGst: vDetails.amountWithoutGst,
    amountWithGst: vDetails.amountWithGst,
    materialStatus: project.materialStatus || ''
  }];
}

export function calculateProjectPOsTotal(pos: VendorPO[]) {
  const count = pos.length;
  const totalWithoutGst = pos.reduce((acc, p) => acc + (Number(p.amountWithoutGst) || 0), 0);
  const totalWithGst = pos.reduce((acc, p) => {
    if (p.amountWithGst !== undefined && p.amountWithGst !== null && Number(p.amountWithGst) > 0) {
      return acc + (Number(p.amountWithGst) || 0);
    }
    const wGst = Number(p.amountWithoutGst) || 0;
    return acc + (wGst > 0 ? Math.round(wGst * 1.18) : 0);
  }, 0);

  return { count, totalWithoutGst, totalWithGst };
}

export function getVendorDetails(project: ProjectTask | null | undefined): VendorDetailsInfo {
  if (!project) {
    return {
      vendorName: '',
      concernedPerson: '',
      mobileNo: '',
      emailId: '',
      poNumberAndDate: '',
      poNumber: '',
      poDate: '',
      amountWithoutGst: null,
      amountWithGst: null
    };
  }

  // If project has multiple vendorPOs, use first/primary PO info with aggregated amounts
  if (Array.isArray(project.vendorPOs) && project.vendorPOs.length > 0) {
    const firstPo = project.vendorPOs[0];
    const totals = calculateProjectPOsTotal(project.vendorPOs);
    const vendorNames = Array.from(new Set(project.vendorPOs.map(p => p.vendorName).filter(Boolean)));
    const poNumbers = project.vendorPOs.map(p => p.poNumber).filter(Boolean);

    let poNumberAndDate = poNumbers.join(', ');
    if (firstPo.poDate) poNumberAndDate += ` (${formatToDDMMYY(firstPo.poDate)})`;

    return {
      vendorName: vendorNames.join(' / ') || firstPo.vendorName,
      concernedPerson: firstPo.concernedPerson || '',
      mobileNo: firstPo.mobileNo || '',
      emailId: firstPo.emailId || '',
      poNumberAndDate,
      poNumber: poNumbers.join(' / '),
      poDate: formatToDDMMYY(firstPo.poDate) || '',
      amountWithoutGst: totals.totalWithoutGst || firstPo.amountWithoutGst || null,
      amountWithGst: totals.totalWithGst || firstPo.amountWithGst || null
    };
  }

  const vendorName = (project.vendorName || '').trim();
  let concernedPerson = (project.vendorContactPerson || '').trim();
  let mobileNo = (project.vendorMobileNo || project.vendorPhone || '').trim();
  if (mobileNo === '-') mobileNo = '';
  let emailId = (project.vendorEmail || '').trim();
  if (emailId === '-') emailId = '';
  const poNumber = (project.poNumber || '').trim();
  const poDate = (project.poDate || '').trim();
  const formattedPoDate = formatToDDMMYY(poDate);

  // If vendor contact details are missing and vendor name is known, match from known vendors
  if (vendorName && vendorName.toLowerCase() !== 'party material') {
    const matched = lookupVendorContact(vendorName);
    if (matched) {
      if (!concernedPerson) concernedPerson = matched.contact;
      if (!emailId && matched.email) emailId = matched.email;
      if (!mobileNo && matched.phone) mobileNo = matched.phone;
    }
  }

  let poNumberAndDate = '';
  if (poNumber && formattedPoDate) {
    poNumberAndDate = `${poNumber} / ${formattedPoDate}`;
  } else if (poNumber) {
    poNumberAndDate = poNumber;
  } else if (formattedPoDate) {
    poNumberAndDate = formattedPoDate;
  }

  const amountWithoutGst = project.poAmount !== undefined && project.poAmount !== null && Number(project.poAmount) > 0
    ? Number(project.poAmount)
    : null;

  const amountWithGst = project.poAmountWithGst !== undefined && project.poAmountWithGst !== null && Number(project.poAmountWithGst) > 0
    ? Number(project.poAmountWithGst)
    : (amountWithoutGst ? Math.round(amountWithoutGst * 1.18) : null);

  return {
    vendorName,
    concernedPerson,
    mobileNo,
    emailId,
    poNumberAndDate,
    poNumber,
    poDate: formattedPoDate,
    amountWithoutGst,
    amountWithGst
  };
}

export interface DeliveryCommitmentInfo {
  displayDate: string;
  isCalculated: boolean;
  status: 'committed_calculated' | 'committed_fixed' | 'awaiting_approval' | 'dispatched_or_completed' | 'tentative';
  subLabel?: string;
  startDate?: string;
  endDate?: string;
}

export function parseDateString(dateStr?: string | number | null): Date | null {
  if (!dateStr) return null;
  const s = String(dateStr).trim();
  if (!s || s === '-' || s.toLowerCase() === 'pending' || s.toLowerCase() === 'not sent' || s.toLowerCase() === 'n/a') {
    return null;
  }

  const dmyMatch = s.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2,4})/);
  if (dmyMatch) {
    const day = parseInt(dmyMatch[1], 10);
    const month = parseInt(dmyMatch[2], 10) - 1;
    let year = parseInt(dmyMatch[3], 10);
    if (year < 100) year += 2000;
    const d = new Date(year, month, day);
    if (!isNaN(d.getTime())) return d;
  }

  const parsed = Date.parse(s);
  if (!isNaN(parsed)) {
    return new Date(parsed);
  }

  return null;
}

/** Formats date into DD/MM/YY (e.g. 15/04/26) */
export function formatDateDDMMYY(d: Date): string {
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = String(d.getFullYear()).slice(-2);
  return `${day}/${month}/${year}`;
}

/** Universal date formatter converting Date, ISO string, DD-MM-YYYY or any date string to DD/MM/YY */
export function formatToDDMMYY(dateStrOrObj?: string | number | Date | null): string {
  if (!dateStrOrObj) return '';
  if (typeof dateStrOrObj === 'object' && dateStrOrObj instanceof Date) {
    if (isNaN(dateStrOrObj.getTime())) return '';
    return formatDateDDMMYY(dateStrOrObj);
  }
  const s = String(dateStrOrObj).trim();
  if (
    !s ||
    s === '-' ||
    s.toLowerCase() === 'pending' ||
    s.toLowerCase() === 'not sent' ||
    s.toLowerCase() === 'n/a' ||
    s.toLowerCase() === 'completed' ||
    s.toLowerCase() === 'dispatched'
  ) {
    return s;
  }

  // If already in DD/MM/YY format (e.g. "01/04/26" or "1/4/26")
  const slash2Match = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2})$/);
  if (slash2Match) {
    return `${slash2Match[1].padStart(2, '0')}/${slash2Match[2].padStart(2, '0')}/${slash2Match[3]}`;
  }

  // If in YYYY-MM-DD or YYYY/MM/DD
  const ymdMatch = s.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})(?:[T\s].*)?$/);
  if (ymdMatch) {
    const year = ymdMatch[1].slice(-2);
    const month = ymdMatch[2].padStart(2, '0');
    const day = ymdMatch[3].padStart(2, '0');
    return `${day}/${month}/${year}`;
  }

  // If in DD-MM-YYYY or DD/MM/YYYY
  const dmyMatch = s.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2,4})(?:[T\s].*)?$/);
  if (dmyMatch) {
    const day = dmyMatch[1].padStart(2, '0');
    const month = dmyMatch[2].padStart(2, '0');
    let year = dmyMatch[3];
    if (year.length === 4) year = year.slice(-2);
    return `${day}/${month}/${year}`;
  }

  // Fallback to parseDateString
  const parsed = parseDateString(s);
  if (parsed) {
    return formatDateDDMMYY(parsed);
  }

  return s;
}

export const formatDateDDMMYYYY = formatDateDDMMYY;

/**
 * Calculates delivery commitment date when drawings are approved.
 * If target specifies e.g. "45 to 60 days after approval" or "30 days after approval",
 * and drawings are approved, it computes the exact target calendar date(s) in DD/MM/YY format.
 */
export function getDeliveryCommitment(
  deliveryTarget?: string,
  drawingApprovedDate?: string,
  drawingSendDate?: string
): DeliveryCommitmentInfo {
  const target = (deliveryTarget || '').trim();
  const approved = (drawingApprovedDate || '').trim();

  if (!target) {
    return {
      displayDate: '-',
      isCalculated: false,
      status: 'tentative'
    };
  }

  if (target.toLowerCase() === 'completed' || target.toLowerCase() === 'dispatched') {
    return {
      displayDate: target,
      isCalculated: false,
      status: 'dispatched_or_completed'
    };
  }

  const rangeMatch = target.match(/(\d+)\s*(?:to|-)\s*(\d+)\s*days?\s*(?:after|from)\s*approval/i);
  const singleMatch = !rangeMatch && target.match(/(\d+)\s*days?\s*(?:after|from)\s*approval/i);
  const weekMatch = !rangeMatch && !singleMatch && target.match(/(\d+)\s*weeks?\s*(?:after|from)\s*approval/i);

  if (rangeMatch || singleMatch || weekMatch) {
    if (approved) {
      const baseDate = parseDateString(approved);
      if (baseDate) {
        if (rangeMatch) {
          const minDays = parseInt(rangeMatch[1], 10);
          const maxDays = parseInt(rangeMatch[2], 10);
          const dMin = new Date(baseDate);
          dMin.setDate(dMin.getDate() + minDays);
          const dMax = new Date(baseDate);
          dMax.setDate(dMax.getDate() + maxDays);

          const fmtMin = formatDateDDMMYY(dMin);
          const fmtMax = formatDateDDMMYY(dMax);

          return {
            displayDate: `${fmtMin} to ${fmtMax}`,
            isCalculated: true,
            status: 'committed_calculated',
            subLabel: `Committed (${minDays}-${maxDays}d from Drg Appr ${formatDateDDMMYY(baseDate)})`,
            startDate: fmtMin,
            endDate: fmtMax
          };
        }

        if (singleMatch) {
          const days = parseInt(singleMatch[1], 10);
          const dTarget = new Date(baseDate);
          dTarget.setDate(dTarget.getDate() + days);
          const fmt = formatDateDDMMYY(dTarget);

          return {
            displayDate: fmt,
            isCalculated: true,
            status: 'committed_calculated',
            subLabel: `Committed (${days}d from Drg Appr ${formatDateDDMMYY(baseDate)})`,
            startDate: fmt
          };
        }

        if (weekMatch) {
          const weeks = parseInt(weekMatch[1], 10);
          const dTarget = new Date(baseDate);
          dTarget.setDate(dTarget.getDate() + weeks * 7);
          const fmt = formatDateDDMMYY(dTarget);

          return {
            displayDate: fmt,
            isCalculated: true,
            status: 'committed_calculated',
            subLabel: `Committed (${weeks}w from Drg Appr ${formatDateDDMMYY(baseDate)})`,
            startDate: fmt
          };
        }
      }
    }

    return {
      displayDate: target,
      isCalculated: false,
      status: 'awaiting_approval',
      subLabel: drawingSendDate ? 'Awaiting Drg Approval' : 'Drg Not Sent'
    };
  }

  if (approved) {
    return {
      displayDate: formatToDDMMYY(target),
      isCalculated: false,
      status: 'committed_fixed',
      subLabel: 'Committed'
    };
  }

  return {
    displayDate: formatToDDMMYY(target),
    isCalculated: false,
    status: 'tentative',
    subLabel: drawingSendDate ? 'Approval Awaited' : undefined
  };
}

export interface StageStatusDetails {
  drawings: DrawingsSubHead | '';
  po: PoSubHead | '';
  material: MaterialSubHead | '';
  production: ProductionSubHead | '';
  activeHead: StageHead;
  activeSubHead: string;
}

export function getProjectStageStatus(project: ProjectTask | null | undefined): StageStatusDetails {
  if (!project) {
    return {
      drawings: 'Send',
      po: 'Not Started',
      material: 'Not Recevied',
      production: '',
      activeHead: 'Drawings',
      activeSubHead: 'Send'
    };
  }

  // 1. Drawings SubHead
  let drawings: DrawingsSubHead | '' = '';
  if (project.stageDrawings === 'Approved' || project.stageDrawings === 'Send') {
    drawings = project.stageDrawings;
  } else if (project.drawingApprovedDate || project.stage === 'Drawing Approved' || project.stage === 'Drawings - Approved') {
    drawings = 'Approved';
  } else if (
    project.stage === 'Fabrication' ||
    project.stage === 'Powder Coating' ||
    project.stage === 'Electrical Wiring' ||
    project.stage === 'Testing' ||
    project.stage === 'Ready for Dispatch' ||
    project.stage === 'Dispatched'
  ) {
    drawings = 'Approved';
  } else {
    drawings = 'Send';
  }

  // 2. PO SubHead
  let po: PoSubHead | '' = '';
  if (project.hasCreatedPO === false) {
    po = 'Not Started';
  } else if (
    project.stagePo === 'Placed' ||
    project.stagePo === 'Draft' ||
    project.stagePo === 'Not Started'
  ) {
    po = project.stagePo;
  } else if (project.vendorPOs && project.vendorPOs.length > 0) {
    const hasPlacedPo = project.vendorPOs.some(
      (vendorPo) => vendorPo.status && vendorPo.status !== 'draft',
    );
    po = hasPlacedPo ? 'Placed' : 'Draft';
  } else if (project.poNumber && project.poNumber.trim() && project.poNumber !== '-' && project.poNumber.toLowerCase() !== 'pending') {
    po = 'Placed';
  } else if (project.stage === 'PO Placed' || project.stage === 'PO - Placed') {
    po = 'Placed';
  } else {
    po = 'Not Started';
  }

  // 3. Material SubHead
  let material: MaterialSubHead | '' = '';
  if (project.stageMaterial === 'Not Recevied' || project.stageMaterial === 'Partially Recevied' || project.stageMaterial === 'Fully recevied') {
    material = project.stageMaterial;
  } else if (project.stage === 'Material Pending' || project.stage === 'Material - Not Recevied') {
    material = 'Not Recevied';
  } else if (project.stage === 'Testing' || project.stage === 'Ready for Dispatch' || project.stage === 'Dispatched' || project.stage === 'Material - Fully recevied') {
    material = 'Fully recevied';
  } else if (project.materialStatus && /all received|fully received|received complete/i.test(project.materialStatus)) {
    material = 'Fully recevied';
  } else if (
    project.stage === 'Fabrication' ||
    project.stage === 'Powder Coating' ||
    project.stage === 'Electrical Wiring' ||
    (project.materialStatus && /received/i.test(project.materialStatus))
  ) {
    material = 'Partially Recevied';
  } else {
    material = 'Not Recevied';
  }

  // 4. Production SubHead
  let production: ProductionSubHead | '' = '';
  if (project.stageProduction && STAGE_HEAD_SUBHEADS['Production'].includes(project.stageProduction)) {
    production = project.stageProduction as ProductionSubHead;
  } else if (project.stage === 'Testing' || project.stage === 'Production - Testing') {
    production = 'Testing';
  } else if (project.stage === 'Electrical Wiring' || project.stage === 'Production - Electrical') {
    production = 'Electrical';
  } else if (project.stage === 'Powder Coating' || project.stage === 'Production - Powder coating') {
    production = 'Powder coating';
  } else if (project.stage === 'Fabrication' || project.stage === 'Production - Cutter') {
    production = 'Cutter';
  } else if (project.stage === 'Ready for Dispatch' || project.stage === 'Dispatched' || project.stage === 'Production - Ready') {
    production = 'Ready';
  } else if (typeof project.stage === 'string' && project.stage.startsWith('Production - ')) {
    production = project.stage.replace('Production - ', '') as ProductionSubHead;
  } else if (drawings === 'Approved' && material !== 'Not Recevied') {
    production = 'Cutter';
  }

  // 5. Active Head & SubHead
  let activeHead: StageHead = 'Drawings';
  let activeSubHead = 'Send';

  if (project.stageHead && project.stageSubHead) {
    activeHead = project.stageHead;
    activeSubHead = project.stageSubHead;
  } else if (typeof project.stage === 'string') {
    if (project.stage.startsWith('Drawings - ')) {
      activeHead = 'Drawings';
      activeSubHead = project.stage.replace('Drawings - ', '');
    } else if (project.stage.startsWith('PO - ')) {
      activeHead = 'PO';
      activeSubHead = project.stage.replace('PO - ', '');
    } else if (project.stage.startsWith('Material - ')) {
      activeHead = 'Material';
      activeSubHead = project.stage.replace('Material - ', '');
    } else if (project.stage.startsWith('Production - ')) {
      activeHead = 'Production';
      activeSubHead = project.stage.replace('Production - ', '');
    } else if (project.stage === 'Drawing Approved') {
      activeHead = 'Drawings';
      activeSubHead = 'Approved';
    } else if (project.stage.includes('Drawing')) {
      activeHead = 'Drawings';
      activeSubHead = 'Send';
    } else if (project.stage === 'PO Placed') {
      activeHead = 'PO';
      activeSubHead = 'Placed';
    } else if (project.stage === 'Material Pending') {
      activeHead = 'Material';
      activeSubHead = 'Not Recevied';
    } else if (project.stage === 'Fabrication') {
      activeHead = 'Production';
      activeSubHead = 'Cutter';
    } else if (project.stage === 'Powder Coating') {
      activeHead = 'Production';
      activeSubHead = 'Powder coating';
    } else if (project.stage === 'Electrical Wiring') {
      activeHead = 'Production';
      activeSubHead = 'Electrical';
    } else if (project.stage === 'Testing') {
      activeHead = 'Production';
      activeSubHead = 'Testing';
    } else if (project.stage === 'Ready for Dispatch' || project.stage === 'Dispatched') {
      activeHead = 'Production';
      activeSubHead = 'Ready';
    } else {
      activeHead = 'Drawings';
      activeSubHead = 'Send';
    }
  }

  return {
    drawings,
    po,
    material,
    production,
    activeHead,
    activeSubHead
  };
}

